import * as React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";

/**
 * Letters the indicator can show. The circle is built to hold up to four
 * quadrants (Termin / Schaden / Vorgang / offener Betrag); for now the backend
 * only emits 'V' (offene Vorgänge + Todos), so a single centred "V" is drawn.
 * The remaining slots stay wired for when T/S/Z are reintroduced.
 */
export type EventTypeKey = "T" | "S" | "V" | "Z";

interface OpenEventsIndicatorProps {
    types: EventTypeKey[];
    onClick?: (e: React.MouseEvent) => void;
    title?: string;
}

const useStyles = makeStyles({
    container: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: tokens.colorNeutralBackground1,
        position: "relative",
        cursor: "pointer",
        overflow: "hidden",
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        ":hover": {
            boxShadow: tokens.shadow4,
        },
        ":focus-visible": {
            outline: `2px solid ${tokens.colorBrandStroke1}`,
            outlineOffset: "1px",
        },
    },
});

// Quadrant text anchors per total count, tuned to read well at 36px.
const getCoordinates = (
    index: number,
    total: number,
): { x: string; y: string; fontSize: string } => {
    if (total <= 1) return { x: "50%", y: "55%", fontSize: "16px" };
    if (total === 2) {
        return index === 0
            ? { x: "25%", y: "55%", fontSize: "12px" }
            : { x: "75%", y: "55%", fontSize: "12px" };
    }
    if (total === 3) {
        if (index === 0) return { x: "25%", y: "30%", fontSize: "8px" };
        if (index === 1) return { x: "75%", y: "30%", fontSize: "8px" };
        return { x: "50%", y: "75%", fontSize: "10px" };
    }
    // total === 4 → Z-pattern: TL, TR, BL, BR
    const quad = [
        { x: "25%", y: "30%" },
        { x: "75%", y: "30%" },
        { x: "25%", y: "75%" },
        { x: "75%", y: "75%" },
    ][index] ?? { x: "50%", y: "50%" };
    return { ...quad, fontSize: "8px" };
};

const renderDividers = (total: number): React.ReactNode => {
    if (total <= 1) return null;
    const stroke = tokens.colorNeutralStroke1;
    if (total === 2) {
        return <line x1="12" y1="0" x2="12" y2="24" stroke={stroke} strokeWidth="1" />;
    }
    if (total === 3) {
        return (
            <>
                <line x1="12" y1="0" x2="12" y2="12" stroke={stroke} strokeWidth="1" />
                <line x1="0" y1="12" x2="24" y2="12" stroke={stroke} strokeWidth="1" />
            </>
        );
    }
    // total === 4 → cross
    return (
        <>
            <line x1="12" y1="0" x2="12" y2="24" stroke={stroke} strokeWidth="1" />
            <line x1="0" y1="12" x2="24" y2="12" stroke={stroke} strokeWidth="1" />
        </>
    );
};

/**
 * Small circular badge listing a contact's open-event letters. Renders nothing
 * when there is nothing open, so callers can drop it in unconditionally.
 */
export const OpenEventsIndicator: React.FC<OpenEventsIndicatorProps> = ({
    types,
    onClick,
    title,
}) => {
    const classes = useStyles();
    if (!types || types.length === 0) return null;

    return (
        <div
            className={classes.container}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClick?.(e as unknown as React.MouseEvent);
                }
            }}
            role="button"
            tabIndex={0}
            title={title}
            aria-label={title ?? `Offene Vorgänge: ${types.join(", ")}`}
        >
            <svg width="36" height="36" viewBox="0 0 24 24">
                {renderDividers(types.length)}
                {types.map((t, i) => {
                    const coords = getCoordinates(i, types.length);
                    return (
                        <text
                            key={`${t}-${i}`}
                            x={coords.x}
                            y={coords.y}
                            fill={tokens.colorNeutralForeground1}
                            fontSize={coords.fontSize}
                            fontWeight="bold"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            pointerEvents="none"
                        >
                            {t}
                        </text>
                    );
                })}
            </svg>
        </div>
    );
};
